import React from 'react';
import HallOfExcellence from '../components/HallOfExcellence';
import Pagination from '../components/Pagination';
import { TrophyIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import AIChatButton from '../components/AIChatButton';

const Gallery: React.FC = () => {
  // Local gallery images from public/galleryimages folder
  const imageLinks = [
    '/galleryimages/D2561D93-6888-4342-9348-1DEF0A795797_1_102_o.jpeg',
    '/galleryimages/4A896B10-F8C1-4390-896C-D70D0660BC35_1_102_o.jpeg',
    '/galleryimages/12E27C74-9A80-4423-9497-17728EA9D7C0_1_102_o.jpeg',
    '/galleryimages/7AD46D2D-0E8C-4110-BF2C-4077ED49A053.jpeg',
    '/galleryimages/ABCC36BB-7969-40BE-9575-D70C1A2FB210.jpeg',
    '/galleryimages/A7150EC5-393A-4B5A-A6D4-74A698197DC0.jpeg',
    '/galleryimages/F5D14C9C-442A-4DF0-9D14-8A1FBE6017DD.jpeg',
    '/galleryimages/C8438649-E246-4256-BC91-6A9B48070938.jpeg',
    '/galleryimages/685D6BE6-A146-4BA4-9256-008B3FB92903.jpeg',
    '/galleryimages/7603066E-F0BA-4335-A62B-13038FC90D47.jpeg',
    '/galleryimages/79CD7703-CA23-4DF4-A6E4-D6CCBBDD93EE.jpeg',
    '/galleryimages/63204FD4-3F1F-48E3-BF02-FF42C3855DB0.jpeg',
    '/galleryimages/7F129BB5-5014-4889-9DD5-5D842B13728C.jpeg',
    '/galleryimages/A976E68D-49BE-4372-85E2-B63ECAAB82AA.jpeg',
    '/galleryimages/EE18138E-6A86-41B2-A2EF-9EFC0902568A.jpeg',
    '/galleryimages/4131758D-9E67-4BDA-B0C5-2CFDF6DC9B88.jpeg',
    '/galleryimages/8AF5004D-D9E9-4E4A-BC42-DE0F6B4BF1DC.jpeg',
    '/galleryimages/B3FB786A-4F58-4311-9A8C-B271A4642110.jpeg',
    '/galleryimages/255FF1CC-B76E-46E1-921B-45537283013B.jpeg',
    '/galleryimages/F9EC7E80-0A15-41BC-BED7-4E1E384A15DA.jpeg',
    '/galleryimages/5875D3B3-F732-4B0A-89CE-C7EC6F4951F4.jpeg',
    '/galleryimages/90E5A858-ACA5-43A1-AB92-F1F3D5AFBD46.jpeg',
    '/galleryimages/CB78662B-D342-4B5B-A2CC-E4E1CC5F61C5.jpeg',
    '/galleryimages/B2E2EE5D-A630-44A8-87AA-AFB495832D30.jpeg',
    '/galleryimages/B7CCC40F-EF32-46FB-93B5-3866212E3704.jpeg'
  ];

  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 12;

  React.useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev === null ? prev : (prev + imageLinks.length - 1) % imageLinks.length));
      }
      if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev === null ? prev : (prev + 1) % imageLinks.length));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, imageLinks.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">

      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <TrophyIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Visual Excellence
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Explore Our</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Achievement Gallery
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Discover inspiring moments, successful graduates, and the vibrant community that makes our learning platform special.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {imageLinks.length}
                  </div>
                  <div className="text-xs text-blue-200">Total Images</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    100%
                  </div>
                  <div className="text-xs text-blue-200">Quality</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    ∞
                  </div>
                  <div className="text-xs text-blue-200">Inspiration</div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => {
                    const galleryGrid = document.querySelector('.grid');
                    galleryGrid?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <MagnifyingGlassPlusIcon className="h-4 w-4 mr-1.5" />
                  View Gallery
                </button>
                <button
                  onClick={() => {
                    const hallOfExcellence = document.querySelector('.bg-gradient-to-br.from-gray-50.to-white');
                    hallOfExcellence?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <TrophyIcon className="h-4 w-4 mr-1.5" />
                  Hall of Excellence
                </button>
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Gallery Preview Cards */}
              <div className="space-y-4">
                {/* Featured Image Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <MagnifyingGlassPlusIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Gallery Preview</h3>
                      <p className="text-blue-200 text-sm">Click to explore images</p>
                    </div>
                  </div>
                </div>

                {/* Achievement Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <TrophyIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Excellence</h3>
                      <p className="text-blue-200 text-sm">Celebrating success</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6 relative z-20 space-y-4 sm:space-y-8">
 

        {/* Gallery Grid */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl mx-3 sm:mx-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {imageLinks
              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
              .map((imageUrl, index) => {
                const actualIndex = (currentPage - 1) * itemsPerPage + index;
                return (
                  <div key={actualIndex} className="group cursor-zoom-in" onClick={() => setLightboxIndex(actualIndex)}>
                    <div className="relative overflow-hidden rounded-2xl shadow-lg border border-gray-200">
                      <img
                        src={imageUrl}
                        alt={`Gallery Image ${actualIndex + 1}`}
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div class=\"w-full  bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center\">
                          <img src="/galleryimages/B7CCC40F-EF32-46FB-93B5-3866212E3704.jpeg" alt="Loading" className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl" />
                            </div>
                          `;
                        }}
                      />
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end justify-between p-3">
                        <div className="text-white text-sm font-medium">Preview</div>
                        <MagnifyingGlassPlusIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(imageLinks.length / itemsPerPage)}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={imageLinks.length}
          />
        </div>
               {/* Hall of Excellence */}
               <section className="py-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl mx-3 sm:mx-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <HallOfExcellence />
          </div>
        </section>
      </section>
      </div>

      {lightboxIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <button
            aria-label="Close"
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            onClick={() => setLightboxIndex(null)}
          >
            <XMarkIcon className="w-7 h-7" />
          </button>

          <button
            aria-label="Previous"
            className="absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            onClick={() => setLightboxIndex((prev) => (prev === null ? prev : (prev + imageLinks.length - 1) % imageLinks.length))}
          >
            <ChevronLeftIcon className="w-8 h-8" />
          </button>

          <img
            src={imageLinks[lightboxIndex!]}
            alt={`Gallery Image ${lightboxIndex! + 1}`}
            className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl"
          />

          <button
            aria-label="Next"
            className="absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            onClick={() => setLightboxIndex((prev) => (prev === null ? prev : (prev + 1) % imageLinks.length))}
          >
            <ChevronRightIcon className="w-8 h-8" />
          </button>

          <div className="absolute bottom-4 text-white/80 text-sm">
            {lightboxIndex! + 1} / {imageLinks.length}
          </div>
        </div>
      )}

      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default Gallery;


